import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { useContent } from '@/content'
import { cn } from '@/lib/cn'
import { Icon, Surface } from '@/components/ui'
import { useSound } from '@/features/audio'
import { ProjectCard } from './ProjectCard'
import styles from './WorkBrowser.module.css'

const ALL = 'all'

/**
 * Every project in one filterable grid.
 *
 * The previous version rendered a separate section per domain, which meant a
 * project classified in three domains appeared three times — sixteen cards
 * for nine projects. One grid with filters shows each project once and still
 * lets a visitor narrow to the discipline they came for.
 *
 * Filter state lives in the URL, so a domain card on the home page can link
 * straight to `?domain=infrastructure`, and any view is shareable and
 * survives the back button.
 */
export const WorkBrowser = () => {
  const { domains, projects } = useContent()
  const [params, setParams] = useSearchParams()
  const { play } = useSound()

  const domain = params.get('domain') ?? ALL
  const category = params.get('category') ?? ALL

  const activeDomain = domains.find((d) => d.id === domain)

  const shown = useMemo(
    () =>
      projects.filter((project) => {
        if (domain === ALL) return true
        return project.classifications.some(
          (c) =>
            c.domain === domain &&
            (category === ALL || c.category === category),
        )
      }),
    [projects, domain, category],
  )

  const countForDomain = (id: string) =>
    id === ALL
      ? projects.length
      : projects.filter((p) => p.classifications.some((c) => c.domain === id))
          .length

  const countForCategory = (categoryId: string) =>
    projects.filter((p) =>
      p.classifications.some(
        (c) => c.domain === domain && c.category === categoryId,
      ),
    ).length

  const select = (nextDomain: string, nextCategory = ALL) => {
    // A filter is a smaller move than a navigation, and gets the smaller
    // cue — the grid re-laying out is the thing being confirmed.
    play('select')

    const next = new URLSearchParams()
    if (nextDomain !== ALL) next.set('domain', nextDomain)
    if (nextCategory !== ALL) next.set('category', nextCategory)
    // `replace` keeps the back button meaning "the previous page", not a
    // walk back through every filter the visitor tried.
    setParams(next, { replace: true })
  }

  return (
    <div className={styles.browser}>
      <Surface pad={20} radius="card" className={styles.controls}>
        <div className={styles.row}>
          <span className={`t-12 ${styles.label}`}>Domain</span>
          <ul className={styles.filters}>
            {[{ id: ALL, name: 'Everything' }, ...domains].map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => select(item.id)}
                  aria-pressed={domain === item.id}
                  className={cn(
                    styles.filter,
                    domain === item.id && styles.filterActive,
                  )}
                >
                  {item.name}
                  <span className={styles.count}>{countForDomain(item.id)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Categories only make sense once a domain is chosen. */}
        <AnimatePresence initial={false}>
          {activeDomain && (
            <motion.div
              key={activeDomain.id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
              className={styles.categoryWrap}
            >
              <div className={styles.row}>
                <span className={`t-12 ${styles.label}`}>Type</span>
                <ul className={styles.filters}>
                  {[{ id: ALL, name: 'All types' }, ...activeDomain.categories].map(
                    (item) => {
                      const count =
                        item.id === ALL
                          ? countForDomain(activeDomain.id)
                          : countForCategory(item.id)
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            disabled={count === 0}
                            onClick={() => select(activeDomain.id, item.id)}
                            aria-pressed={category === item.id}
                            className={cn(
                              styles.filter,
                              category === item.id && styles.filterActive,
                            )}
                          >
                            {item.name}
                            <span className={styles.count}>{count}</span>
                          </button>
                        </li>
                      )
                    },
                  )}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Surface>

      <p className={`t-12 ${styles.summary}`} role="status">
        {shown.length} {shown.length === 1 ? 'project' : 'projects'}
        {activeDomain ? ` in ${activeDomain.name}` : ''}
      </p>

      {shown.length > 0 ? (
        <motion.ul layout className={styles.grid}>
          <AnimatePresence mode="popLayout">
            {shown.map((project) => (
              <motion.li
                key={project.slug}
                layout
                initial={{ opacity: 0, transform: 'scale(0.96)' }}
                animate={{ opacity: 1, transform: 'scale(1)' }}
                exit={{ opacity: 0, transform: 'scale(0.96)' }}
                transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
              >
                <ProjectCard project={project} size="small" />
              </motion.li>
            ))}
          </AnimatePresence>
        </motion.ul>
      ) : (
        <Surface pad={30} radius="card" className={styles.empty}>
          <Icon name="briefcase" size={22} />
          <p className="t-14">Nothing published under this filter yet.</p>
        </Surface>
      )}
    </div>
  )
}
